
import { Vue, Component, Prop, Watch } from 'vue-property-decorator';
import TableFilterable from '@/components/reports-v3/components/filterables/TableFilterable.vue';
import FilterWidget from '@/components/reports-v3/components/codedWidgets/shared/FilterWidget.vue';
import {
	PermissionsGroup,
} from '@/store/models.def';
import gdbx from '@/store/modules/gdbx';
import { roundToFixed } from '@/util/number';
import { TableItemFormatter } from '@/components/reports-v3/components/elements/charts/helpers/tableItemFormatter';
import TableCollapseToggle from '@/components/TableCollapseToggle.vue';
import moment from 'moment';
import { getTableName, TIMEZONE_OFFSET_MS } from '@/worker/db/sqlHelpers';

@Component({
	components: {
		TableFilterable,
		TableCollapseToggle,
	},
})
export default class BpExpensesTable extends FilterWidget {
	public get permissionIds(): PermissionsGroup[] {
		return ['transactions'];
	}

	public filterIds: Array<
		| 'date'
		| 'dateAsOf'
		| 'dateRange'
		| 'stockItems'
		| 'agents'
		| 'customers'
		| 'suppliers'
    | 'projects'
	> = ['dateRange'];

	public tableItems: any[] = [];
	public tableFields: any[] = [];
	public periodTotalData: Array<{ month: number; total: number }> = [];
	public periodTotalFields: any[] = [];

	public get exportFileName() {
		const formattedDate = [
			moment(this.selectedDateRange[0]).format('DD MMM YY'),
			moment(this.selectedDateRange[1]).format('DD MMM YY'),
		];
		return (
			'Expenses' + '_' + formattedDate[0] + ' to ' + formattedDate[1]
		);
	}

	public get dateFormatted() {
		return this.selectedDateRange;
	}

	public get currencySymbol() {
		return gdbx.currencySymbol;
	}

	public get numDecimal() {
		return gdbx.numDecimal;
	}

	public get expensiveHook() {
		const {
      selectedDateRange,
      selectedProjects,
    } = this;
		return JSON.stringify([
      selectedDateRange,
      selectedProjects,
    ]);
	}

	public async expensiveCalc() {
		const escapeStr = (s: string) => s.replace(/'/g, '\'\'');
		const table = getTableName('transactions');
		const range = this.selectedDateRange;
		const amt = 'TRY_CAST("AMOUNT" AS DOUBLE)';
		const dateExpr = 'TRY_CAST("DOCDATE" AS BIGINT)';

		let projectFilter = '';
		if (this.selectedProjects.length > 0) {
			const list = this.selectedProjects
				.map((p) => `'${escapeStr(p)}'`).join(',');
			projectFilter = ` AND "PROJECT" IN (${list})`;
		}

		const jobId = this.currentJobId;
		const queryHead = await this._ensureTablesLoaded(
			['transactions'],
		);
		this.checkInterrupted(jobId);

		this.generateLoadingText(0.3);

		// Single cross-tab query: specialAccTypeCode x month
		const yearExpr = `EXTRACT(YEAR FROM EPOCH_MS(`
			+ `${dateExpr} + ${TIMEZONE_OFFSET_MS}))`;
		const monthExpr = `EXTRACT(MONTH FROM EPOCH_MS(`
			+ `${dateExpr} + ${TIMEZONE_OFFSET_MS}))`;

		const crossResult = await queryHead.rawQuery(`
			SELECT "SPECIALACCTYPECODE" as code,
				"SPECIALACCTYPENAME" as name,
				${yearExpr} as year,
				${monthExpr} as month,
				SUM(${amt}) as total
			FROM ${table}
			WHERE ${dateExpr}
				BETWEEN ${range[0]} AND ${range[1]}
				AND "ACCTYPE" = 'EP'
				${projectFilter}
			GROUP BY code, name, year, month
		`);
		this.checkInterrupted(jobId);

		// Build month labels from date range
		const startMoment = moment(range[0]).startOf('month');
		const endMoment = moment(range[1]).endOf('month');
		const months: Array<{
			label: string; year: number;
			month: number; endMs: number;
		}> = [];
		const mm = startMoment.clone();
		while (mm.isSameOrBefore(endMoment, 'month')) {
			months.push({
				label: mm.format('MMM YYYY'),
				year: mm.year(),
				month: mm.month() + 1,
				endMs: mm.clone().endOf('month').valueOf(),
			});
			mm.add(1, 'month');
		}

		// Build lookups
		const codeNameMap = new Map<string, string>();
		const crossTab: Record<string, number> = {};
		for (const row of crossResult) {
			const code = String(row.code);
			codeNameMap.set(code, String(row.name));
			const key = `${code}-${Number(row.year)}`
				+ `-${Number(row.month)}`;
			crossTab[key] = Number(row.total) || 0;
		}
		const codes = [...codeNameMap.entries()];

		// Build table fields
		this.tableFields = [
			{
				key: 'particular',
				sortable: true,
				stickyColumn: true,
			},
			{
				key: 'total',
				sortable: true,
				formatter: TableItemFormatter.currencySplit,
			},
			...months.map((m) => ({
				key: m.label,
				sortable: true,
				formatter: TableItemFormatter.currencySplit,
			})),
		];

		this.periodTotalFields = [
			{
				key: 'month', sortable: true,
				formatter: TableItemFormatter.date2,
				textAlign: 'center',
			},
			{
				key: 'total', sortable: true,
				formatter: TableItemFormatter.currencySplit,
				textAlign: 'center',
			},
		];

		// Build per-month value arrays for top-5 highlighting
		const monthValueArrays: number[][] = months.map((m) =>
			codes.map(([code]) =>
				crossTab[`${code}-${m.year}-${m.month}`] || 0,
			),
		);

		// Build table items
		this.tableItems = codes.map(([code, name]) => {
			const item: any = {
				particular: name,
				total: 0,
				_cellVariants: {},
			};

			months.forEach((m, mIndex) => {
				const val = crossTab[
					`${code}-${m.year}-${m.month}`
				] || 0;
				item[m.label] = val;
				item.total += val;

				// Top-5 highlighting
				const sorted = monthValueArrays[mIndex]
					.slice().sort((a, b) => b - a)
					.filter((v) => v !== 0)
					.slice(0, 5);
				item._cellVariants[m.label] =
					sorted.includes(val) ? 'top-five' : '';
			});

			return item;
		});

		// Period total data
		this.periodTotalData = months.map((m) => {
			const total = codes.reduce((sum, [code]) => {
				return sum + (crossTab[
					`${code}-${m.year}-${m.month}`
				] || 0);
			}, 0);
			return { month: m.endMs, total };
		});

		this.generateLoadingText(1);

		this.saveHistory(
			'tableItems',
			'tableFields',
			'periodTotalData',
			'periodTotalFields',
		);
	}
}
